import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { motion, AnimatePresence } from "framer-motion";
import logo from "@/assets/logo.webp";

const navLinks = [
  { name: "Home", path: "/" },
  { name: "About", path: "/about" },
  { name: "Services", path: "/services" },
  { name: "Gallery", path: "/gallery" },
  { name: "FAQ", path: "/faq" },
  { name: "Contact", path: "/contact" },
];

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 border-b border-border/20 transition-all duration-300 ${
      isScrolled ? "bg-charcoal/90 backdrop-blur-md" : "bg-charcoal"
    }`}>
      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8">
        <div className="flex items-center justify-between h-14 sm:h-16 md:h-20">
          <Link to="/" className="flex h-full flex-shrink-0 items-center">
            <div className="flex h-full items-center">
              <img 
                src={logo}
                srcSet={`/logo-160.webp 347w, ${logo} 1388w`}
                sizes="(max-width: 640px) 200px, 347px"
                alt="Lawnworks Of NWA Logo"
                width={347}
                height={40}
                className="h-auto w-auto max-w-full max-h-9 sm:max-h-10 md:max-h-12"
              />
            </div>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`font-medium transition-colors hover:text-accent-light ${
                  location.pathname === link.path
                    ? "text-accent-light"
                    : "text-primary-foreground/90"
                }`}
              >
                {link.name}
              </Link>
            ))}
          </nav>

          {/* Desktop CTA */}
          <div className="hidden md:flex items-center gap-3">
            <a href="tel:+14794649398">
              <Button variant="outline" size="sm" className="border-accent-light text-accent-light hover:bg-accent-light hover:text-charcoal">
                <Phone className="h-4 w-4 mr-2" />
                Call
              </Button>
            </a>

            <Link to="/contact">
              <Button variant="cta" size="sm">
                Free Estimate
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-primary-foreground p-2"
            onClick={() => setIsOpen(!isOpen)}
            aria-label="Toggle menu"
          >
            {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-charcoal border-t border-border/20"
          >
            <nav className="container-custom mx-auto px-4 py-6 flex flex-col gap-4">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsOpen(false)}
                  className={`font-medium text-lg py-2 transition-colors ${
                    location.pathname === link.path
                      ? "text-accent-light"
                      : "text-primary-foreground/80"
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              <div className="flex flex-col gap-3 pt-4 border-t border-border/20">
                <a
                  href="tel:+14794649398"
                 
                  className="flex items-center gap-2 text-primary-foreground/80"
                >
                  <Phone className="h-5 w-5" />
                  <span className="font-medium">479-464-9398</span>
                </a>
                <Link to="/contact" onClick={() => setIsOpen(false)}>
                  <Button variant="cta" className="w-full">
                    Get Free Estimate
                  </Button>
                </Link>
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
